import { useEffect, useMemo, useState } from "react";
import {
    AlertCircle,
    Box,
    Clock,
    Flame,
    Layers,
    MessageCircle,
    MousePointerClick,
    Radio,
    Send,
    Users,
} from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    useBroadcastsList,
    useClicks,
    useEsteiraList,
    useFlowsList,
} from "../api/queries";
import { useDashboardStore } from "../store/dashboardStore";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { SectionCard } from "../components/SectionCard";
import { KpiCard } from "../components/KpiCard";
import { Segmented } from "../components/Segmented";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { DashSkeleton, SkelText } from "../components/Skeleton";
import { ApiBanner } from "../components/ApiBanner";
import { BadgeHi } from "../components/BadgeHi";
import { fmt, fmtPct } from "../lib/format";
import { cn } from "@/lib/utils";
import type { ClickStep } from "../types/reports";

type ScopeType = "all" | "flow" | "esteira" | "broadcast";

const SCOPE_OPTIONS: Array<{ v: ScopeType; l: string; icon: typeof Layers }> = [
    { v: "all", l: "Geral", icon: Layers },
    { v: "flow", l: "Flow", icon: MessageCircle },
    { v: "esteira", l: "Esteira", icon: Radio },
    { v: "broadcast", l: "Broadcast", icon: Send },
];

export function ClicksView() {
    const cliquesView = useDashboardStore((s) => s.toggles.cliquesView);
    const horaView = useDashboardStore((s) => s.toggles.horaView);
    const setToggle = useDashboardStore((s) => s.setToggle);

    const [scopeType, setScopeType] = useState<ScopeType>("all");
    const [scopeId, setScopeId] = useState<string | null>(null);
    const [selectedStepId, setSelectedStepId] = useState<string | null>(null);

    // Reset selection when scope changes
    useEffect(() => {
        setSelectedStepId(null);
    }, [scopeType, scopeId]);

    useEffect(() => {
        // Reset id when scope type changes
        setScopeId(null);
    }, [scopeType]);

    const { data, error, isLoading, refetch } = useClicks({
        type: scopeType,
        id: scopeId,
    });
    const loading = isLoading && !data;

    const hero = (
        <PageHeader
            icon={MousePointerClick}
            title="Cliques"
            subtitle="Distribuição por step, hora e por origem"
        />
    );

    if (error && !data) {
        return (
            <PageShell hero={hero}>
                <ApiBanner error={error} onRetry={() => refetch()} />
            </PageShell>
        );
    }

    const kpis = data?.kpis;
    const steps: ClickStep[] = data?.steps ?? [];
    const byHour = data?.byHour ?? [];
    const heatmap = data?.heatmap ?? [];

    // Total clicks (KPI takes precedence, fallback to sum of steps)
    const totalStepClicks = steps.reduce((a, s) => a + (s.clicks || 0), 0);
    const totalClicks = kpis?.totalClicks ?? totalStepClicks;
    const uniqueLeads = kpis?.uniqueLeads ?? 0;
    const ctr = kpis?.ctr ?? 0;

    // Derived: sharePct per step + isHot flag (top 3)
    const stepsAug = useMemo(() => {
        if (!steps.length) return [];
        const sorted = [...steps].sort((a, b) => (b.clicks || 0) - (a.clicks || 0));
        const sum = sorted.reduce((a, s) => a + (s.clicks || 0), 0) || 1;
        return sorted.map((s, i) => ({
            ...s,
            sharePct: ((s.clicks || 0) / sum) * 100,
            isHot: i < 3,
            rank: i + 1,
        }));
    }, [steps]);

    // Peak hour
    const peakHour = useMemo(() => {
        if (!byHour.length) return null;
        return byHour.reduce(
            (acc, r) => ((r.clicks || 0) > (acc.clicks || 0) ? r : acc),
            byHour[0]
        );
    }, [byHour]);

    // Top step (most clicks)
    const topStep = stepsAug[0];

    // Active selected step
    const activeStep =
        stepsAug.find((s) => s.id === selectedStepId) ?? stepsAug[0];

    // Prefetch preview images dos top 12 steps — o user navega no ranking
    // clicando nas linhas; sem prefetch, a foto piscava a cada troca enquanto
    // o browser baixava a nova URL do CDN do Meta.
    useEffect(() => {
        const urls = stepsAug
            .slice(0, 12)
            .map((s) => s.previewMediaUrl)
            .filter((u): u is string => !!u);
        urls.forEach((url) => {
            const img = new Image();
            img.src = url;
        });
    }, [stepsAug]);

    return (
        <PageShell hero={hero}>
            {/* Scope selector */}
            <ScopeSelector
                scopeType={scopeType}
                scopeId={scopeId}
                onScopeType={setScopeType}
                onScopeId={setScopeId}
            />

            {/* KPI strip */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KpiCard
                    icon={MousePointerClick}
                    iconColor="text-violet-500"
                    label="Cliques · período"
                    value={loading ? <SkelText /> : fmt(Math.round(totalClicks))}
                    sub={ctr > 0 ? `CTR médio ${fmtPct(ctr)}` : "no período"}
                />
                <KpiCard
                    icon={Users}
                    iconColor="text-blue-500"
                    label="Leads únicos"
                    value={loading ? <SkelText /> : fmt(Math.round(uniqueLeads))}
                    sub="contatos clicaram"
                />
                <KpiCard
                    icon={Flame}
                    iconColor="text-amber-500"
                    label="Step mais clicado"
                    value={loading ? <SkelText /> : topStep?.label ?? "—"}
                    sub={
                        topStep
                            ? `${fmt(topStep.clicks)} · ${topStep.sharePct.toFixed(1)}%`
                            : ""
                    }
                />
            </div>

            {/* Cliques por hora */}
            <SectionCard
                icon={Clock}
                title="Cliques por hora"
                subtitle={
                    peakHour
                        ? `pico ${String(peakHour.hour).padStart(2, "0")}h`
                        : ""
                }
                right={
                    <Segmented<"bars" | "heat">
                        value={horaView}
                        onChange={(v) => setToggle("horaView", v)}
                        options={[
                            { v: "bars", l: "Barras 24h" },
                            { v: "heat", l: "Heatmap dia×h" },
                        ]}
                    />
                }
            >
                {loading ? (
                    <DashSkeleton h={288} />
                ) : horaView === "bars" ? (
                    <HourBars byHour={byHour} peakHour={peakHour?.hour ?? null} byDateHour={data?.byDateHour} />
                ) : (
                    <HourHeatmap cells={heatmap} />
                )}
            </SectionCard>

            {/* Cliques por step — Torre / Barras 3D */}
            <SectionCard
                icon={Box}
                title="Cliques por step"
                subtitle={
                    stepsAug.length
                        ? `${stepsAug.length} steps · top ${Math.min(3, stepsAug.length)} concentra ${stepsAug
                              .slice(0, 3)
                              .reduce((a, s) => a + s.sharePct, 0)
                              .toFixed(0)}%`
                        : "visualização 3D"
                }
                right={
                    <Segmented<"torre" | "bars">
                        value={cliquesView}
                        onChange={(v) => setToggle("cliquesView", v)}
                        options={[
                            { v: "torre", l: "Torre" },
                            { v: "bars", l: "Barras 3D" },
                        ]}
                    />
                }
            >
                {loading ? (
                    <DashSkeleton h={480} />
                ) : stepsAug.length === 0 ? (
                    <EmptyMsg />
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
                        <div>
                            {cliquesView === "torre" ? (
                                <TowerView
                                    steps={stepsAug.slice(0, 9)}
                                    selectedId={activeStep?.id ?? null}
                                    onSelect={setSelectedStepId}
                                />
                            ) : (
                                <Bars3DView
                                    steps={stepsAug.slice(0, 12)}
                                    selectedId={activeStep?.id ?? null}
                                    onSelect={setSelectedStepId}
                                />
                            )}
                        </div>

                        {/* Right column — ranking + preview */}
                        <div className="space-y-4">
                            <div>
                                <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider mb-2">
                                    Ranking · clique pra ver preview
                                </p>
                                <div className="space-y-1 max-h-[240px] overflow-y-auto -mx-1 px-1">
                                    {stepsAug.slice(0, 12).map((h, i) => (
                                        <button
                                            key={h.id}
                                            onClick={() => setSelectedStepId(h.id)}
                                            className={cn(
                                                "w-full flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded-md transition-colors",
                                                activeStep?.id === h.id
                                                    ? "bg-violet-50 ring-1 ring-violet-200"
                                                    : "hover:bg-gray-50"
                                            )}
                                        >
                                            <span className="text-[10px] font-mono text-gray-400 w-5 shrink-0">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <span
                                                className={cn(
                                                    "truncate flex-1 min-w-0",
                                                    h.isHot
                                                        ? "text-gray-900 font-semibold"
                                                        : "text-gray-700"
                                                )}
                                            >
                                                {h.label}
                                            </span>
                                            <span className="text-xs font-mono text-gray-500 shrink-0">
                                                {fmt(h.clicks)}
                                            </span>
                                            <span
                                                className={cn(
                                                    "text-[11px] font-semibold px-1.5 py-0.5 rounded shrink-0",
                                                    h.isHot
                                                        ? "bg-amber-100 text-amber-700"
                                                        : "bg-gray-100 text-gray-600"
                                                )}
                                            >
                                                {h.sharePct.toFixed(1)}%
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {activeStep && (
                                <StepPreview
                                    key={`${scopeType}-${scopeId ?? "all"}-${activeStep.id}`}
                                    step={activeStep}
                                />
                            )}
                        </div>
                    </div>
                )}
            </SectionCard>
        </PageShell>
    );
}

// ── Scope selector ───────────────────────────────────────────
interface ScopeSelectorProps {
    scopeType: ScopeType;
    scopeId: string | null;
    onScopeType: (t: ScopeType) => void;
    onScopeId: (id: string | null) => void;
}

function ScopeSelector({
    scopeType,
    scopeId,
    onScopeType,
    onScopeId,
}: ScopeSelectorProps) {
    // Fetch lists conditionally
    const flowsQ = useFlowsList();
    const esteiraQ = useEsteiraList();
    const broadcastsQ = useBroadcastsList(1, 200, "none");

    const options = useMemo(() => {
        if (scopeType === "flow") {
            return (flowsQ.data?.items ?? []).map((f) => ({
                v: f.id,
                l: f.name || `#${f.id}`,
            }));
        }
        if (scopeType === "esteira") {
            return (esteiraQ.data?.items ?? []).map((e) => ({
                v: e.id,
                l: e.name || `#${e.id}`,
            }));
        }
        if (scopeType === "broadcast") {
            return (broadcastsQ.data?.items ?? []).map((b) => ({
                v: b.id,
                l: b.name || `#${b.id}`,
            }));
        }
        return [];
    }, [scopeType, flowsQ.data, esteiraQ.data, broadcastsQ.data]);

    const selectedLabel = options.find((o) => o.v === scopeId)?.l;
    const subListLoading =
        (scopeType === "flow" && flowsQ.isLoading) ||
        (scopeType === "esteira" && esteiraQ.isLoading) ||
        (scopeType === "broadcast" && broadcastsQ.isLoading);

    return (
        <div className="bg-white border border-gray-100 rounded-xl p-3 flex flex-col gap-3 md:flex-row md:items-center md:flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mr-1">
                    Origem
                </span>
                {SCOPE_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const active = scopeType === opt.v;
                    return (
                        <button
                            key={opt.v}
                            onClick={() => onScopeType(opt.v)}
                            className={cn(
                                "inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors",
                                active
                                    ? "bg-violet-600 border-violet-600 text-white shadow-sm"
                                    : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                            )}
                        >
                            <Icon className="w-3.5 h-3.5" />
                            {opt.l}
                        </button>
                    );
                })}
                {scopeType !== "all" && scopeId && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-violet-50 border border-violet-200 text-violet-700 px-2.5 py-1.5 rounded-lg max-w-[280px]">
                        <span className="truncate">
                            {selectedLabel ?? `#${scopeId}`}
                        </span>
                        <button
                            onClick={() => onScopeId(null)}
                            className="hover:bg-violet-100 rounded p-0.5"
                            aria-label="Limpar seleção"
                        >
                            ×
                        </button>
                    </span>
                )}
                <span className="text-[11px] text-gray-400 ml-1">
                    {scopeType === "all"
                        ? "todos os cliques"
                        : scopeId
                          ? `${SCOPE_OPTIONS.find((o) => o.v === scopeType)?.l.toLowerCase()} selecionado`
                          : `escolha um ${SCOPE_OPTIONS.find((o) => o.v === scopeType)?.l.toLowerCase()}`}
                </span>
            </div>

            {scopeType !== "all" && (
                <div className="md:ml-auto w-full md:w-[300px]">
                    {subListLoading ? (
                        <DashSkeleton h={36} />
                    ) : (
                        <SearchableSelect
                            value={scopeId ?? undefined}
                            onValueChange={(v) => onScopeId(v || null)}
                            options={options.map((o) => ({ value: o.v, label: o.l }))}
                            placeholder={`Escolha um ${SCOPE_OPTIONS.find((o) => o.v === scopeType)?.l.toLowerCase()}…`}
                            searchPlaceholder={`Buscar ${SCOPE_OPTIONS.find((o) => o.v === scopeType)?.l.toLowerCase()}...`}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

// ── Step preview card (Messenger style) ──────────────────────
interface StepAug extends ClickStep {
    sharePct: number;
    isHot: boolean;
    rank: number;
}

function StepPreview({ step }: { step: StepAug }) {
    const hasImage = !!step.previewMediaUrl;
    const hasContent =
        hasImage || step.previewTitle || step.previewSubtitle;

    return (
        <div className="border-t border-gray-100 pt-3">
            <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider">
                    Preview do step
                </p>
                {step.isHot && <BadgeHi color="amber">hot</BadgeHi>}
            </div>
            <p className="text-sm font-semibold text-gray-800 truncate">
                {step.label}
            </p>
            <p className="text-[11px] text-gray-400 mb-3">
                {step.flowName && <span>flow {step.flowName} · </span>}
                node #{step.id}
            </p>

            {hasContent ? (
                <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg p-2.5">
                    <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                        {hasImage && (
                            <div
                                className="bg-slate-200 overflow-hidden"
                                style={{ aspectRatio: "1/1" }}
                            >
                                <img
                                    src={step.previewMediaUrl ?? ""}
                                    alt=""
                                    className="object-cover w-full h-full"
                                    onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                    }}
                                />
                            </div>
                        )}
                        {(step.previewTitle || step.previewSubtitle) && (
                            <div className="px-3 py-2 space-y-0.5">
                                {step.previewTitle && (
                                    <p className="text-[12px] font-bold text-gray-900 leading-snug line-clamp-2">
                                        {step.previewTitle}
                                    </p>
                                )}
                                {step.previewSubtitle && (
                                    <p className="text-[11px] text-gray-600 leading-snug line-clamp-3">
                                        {step.previewSubtitle}
                                    </p>
                                )}
                            </div>
                        )}
                        {Array.isArray((step as ClickStep & { buttons?: { text: string }[] }).buttons) &&
                            ((step as ClickStep & { buttons?: { text: string }[] }).buttons?.length ?? 0) > 0 && (
                                <div className="border-t border-gray-100 divide-y divide-gray-100">
                                    {((step as ClickStep & { buttons?: { text: string }[] }).buttons ?? []).map((b, i) => (
                                        <div
                                            key={i}
                                            className="px-3 py-2 text-[12px] text-center text-blue-600 font-medium truncate"
                                        >
                                            {b.text || "—"}
                                        </div>
                                    ))}
                                </div>
                            )}
                    </div>
                </div>
            ) : (
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 flex items-center gap-2 text-xs text-gray-500">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>Preview indisponível para este step</span>
                </div>
            )}

            <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div>
                    <p className="text-[10px] text-gray-400 uppercase">
                        Cliques
                    </p>
                    <p className="font-bold text-violet-600 font-mono">
                        {fmt(step.clicks)}
                    </p>
                </div>
                <div>
                    <p className="text-[10px] text-gray-400 uppercase">
                        % total
                    </p>
                    <p className="font-bold text-gray-900 font-mono">
                        {step.sharePct.toFixed(1)}%
                    </p>
                </div>
                {step.revenue != null && (
                    <div>
                        <p className="text-[10px] text-gray-400 uppercase">$</p>
                        <p className="font-bold text-emerald-600 font-mono">
                            ${Number(step.revenue).toFixed(0)}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Torre 3D (CSS perspective) ───────────────────────────────
interface TowerViewProps {
    steps: StepAug[];
    selectedId: string | null;
    onSelect: (id: string) => void;
}

function TowerView({ steps, selectedId, onSelect }: TowerViewProps) {
    if (!steps.length) return <EmptyMsg />;
    const max = steps[0].clicks || 1;
    // Escala raiz quadrada: amplifica diferenças nos valores baixos (sem
    // raiz, o top step gigante achata visualmente todos os outros — 280
    // cliques ficava com largura quase idêntica a 506 cliques).
    const widthFor = (clicks: number) => {
        const ratio = Math.max(0, Math.min(1, (clicks || 0) / max));
        return 110 + Math.sqrt(ratio) * 360;
    };
    return (
        <div
            className="rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 p-8 flex justify-center items-start"
            style={{ minHeight: 480, perspective: "1100px" }}
        >
            <div
                style={{
                    transformStyle: "preserve-3d",
                    transform: "rotateX(22deg) rotateY(-12deg)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                {steps.map((st, i) => {
                    const w = widthFor(st.clicks || 0);
                    const h = 56;
                    const isSelected = selectedId === st.id;
                    const front = st.isHot
                        ? "linear-gradient(180deg, #c4b5fd 0%, #8b5cf6 100%)"
                        : "#ffffff";
                    const side = st.isHot ? "#6d28d9" : "#e5e7eb";
                    const top = st.isHot ? "#ddd6fe" : "#f8fafc";
                    const borderColor = isSelected
                        ? "#2563eb"
                        : st.isHot
                          ? "#7c3aed"
                          : "#cbd5e1";
                    return (
                        <div
                            key={st.id}
                            onClick={() => onSelect(st.id)}
                            style={{
                                width: w,
                                height: h,
                                background: front,
                                border: `${isSelected ? 2.5 : 1.5}px solid ${borderColor}`,
                                position: "relative",
                                transformStyle: "preserve-3d",
                                boxShadow: isSelected
                                    ? "0 4px 16px rgba(37,99,235,0.25)"
                                    : "0 2px 8px rgba(15,23,42,0.06)",
                                color: st.isHot ? "#ffffff" : "#334155",
                                cursor: "pointer",
                            }}
                        >
                            {/* right side panel */}
                            <div
                                style={{
                                    position: "absolute",
                                    right: -22,
                                    top: 0,
                                    width: 22,
                                    height: h,
                                    background: side,
                                    borderTop: `1.5px solid ${st.isHot ? "#6d28d9" : "#cbd5e1"}`,
                                    borderRight: `1.5px solid ${st.isHot ? "#6d28d9" : "#cbd5e1"}`,
                                    transform: "rotateY(60deg)",
                                    transformOrigin: "left center",
                                }}
                            />
                            {/* top panel */}
                            <div
                                style={{
                                    position: "absolute",
                                    left: 0,
                                    top: -16,
                                    width: w,
                                    height: 16,
                                    background: top,
                                    borderLeft: `1.5px solid ${st.isHot ? "#6d28d9" : "#cbd5e1"}`,
                                    borderTop: `1.5px solid ${st.isHot ? "#6d28d9" : "#cbd5e1"}`,
                                    transform: "rotateX(60deg)",
                                    transformOrigin: "bottom center",
                                }}
                            />
                            <div className="flex items-center justify-between h-full px-4">
                                <div>
                                    <div className="text-[10px] uppercase font-medium opacity-70">
                                        step #{i + 1}
                                    </div>
                                    <div
                                        className="text-sm font-bold truncate"
                                        style={{ maxWidth: w - 100 }}
                                    >
                                        {st.label}
                                    </div>
                                </div>
                                <div className="text-lg font-bold font-mono">
                                    {st.clicks >= 1000
                                        ? (st.clicks / 1000).toFixed(1) + "k"
                                        : st.clicks}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ── Barras 3D (isometric SVG) ────────────────────────────────
function Bars3DView({ steps, selectedId, onSelect }: TowerViewProps) {
    if (!steps.length) return <EmptyMsg />;
    const max = Math.max(...steps.map((s) => s.clicks || 0), 1);
    return (
        <div
            className="rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 p-6"
            style={{ minHeight: 480 }}
        >
            <svg
                viewBox="0 0 800 460"
                width="100%"
                height="460"
                preserveAspectRatio="xMidYMid meet"
            >
                <g transform="translate(40 380)">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <line
                            key={i}
                            x1="0"
                            y1={-i * 70}
                            x2="740"
                            y2={-i * 70}
                            stroke="#e2e8f0"
                            strokeDasharray="3 4"
                        />
                    ))}
                    <line
                        x1="0"
                        y1="0"
                        x2="740"
                        y2="0"
                        stroke="#94a3b8"
                        strokeWidth="1.4"
                    />
                </g>
                {steps.map((s, i) => {
                    const h = ((s.clicks || 0) / max) * 290;
                    const x = 60 + i * 60;
                    const baseY = 380;
                    const isHot = s.isHot;
                    const front = isHot ? "#8b5cf6" : "#a78bfa";
                    const side = isHot ? "#6d28d9" : "#7c3aed";
                    const top = isHot ? "#c4b5fd" : "#ddd6fe";
                    const isSelected = selectedId === s.id;
                    return (
                        <g
                            key={s.id}
                            onClick={() => onSelect(s.id)}
                            style={{ cursor: "pointer" }}
                        >
                            <polygon
                                points={`${x},${baseY} ${x + 40},${baseY} ${x + 40},${baseY - h} ${x},${baseY - h}`}
                                fill={front}
                                stroke={isSelected ? "#2563eb" : "none"}
                                strokeWidth="2"
                            />
                            <polygon
                                points={`${x + 40},${baseY} ${x + 56},${baseY - 12} ${x + 56},${baseY - h - 12} ${x + 40},${baseY - h}`}
                                fill={side}
                                stroke={isSelected ? "#2563eb" : "none"}
                                strokeWidth="2"
                            />
                            <polygon
                                points={`${x},${baseY - h} ${x + 40},${baseY - h} ${x + 56},${baseY - h - 12} ${x + 16},${baseY - h - 12}`}
                                fill={top}
                                stroke={isSelected ? "#2563eb" : "none"}
                                strokeWidth="2"
                            />
                            <rect
                                x={x - 4}
                                y={baseY - h - 14}
                                width={62}
                                height={h + 30}
                                fill="transparent"
                            />
                            <text
                                x={x + 20}
                                y={baseY - h - 18}
                                textAnchor="middle"
                                fontSize="10"
                                fontWeight="700"
                                fill="#1f2937"
                            >
                                {s.clicks >= 1000
                                    ? (s.clicks / 1000).toFixed(1) + "k"
                                    : s.clicks}
                            </text>
                            <text
                                x={x + 20}
                                y={baseY + 18}
                                textAnchor="middle"
                                fontSize="9"
                                fill="#64748b"
                                transform={`rotate(-35 ${x + 20} ${baseY + 18})`}
                            >
                                {(s.label || "").slice(0, 12)}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

// ── Hour bars ─────────────────────────────────────────────────
interface HourBarsProps {
    byHour: Array<{ hour: number; clicks: number }>;
    peakHour: number | null;
    byDateHour?: Array<{ date: string; hour: number; clicks: number }>;
}

function HourBars({ byHour, peakHour, byDateHour }: HourBarsProps) {
    if (!byHour.length) return <EmptyMsg />;
    const data = byHour.map((row) => ({
        h: String(row.hour).padStart(2, "0"),
        v: row.clicks,
        hour: row.hour,
    }));
    // Tooltip mostra breakdown por data só quando filtro cobre múltiplos dias.
    const uniqueDates = new Set((byDateHour || []).map((r) => r.date));
    const isMultiDay = uniqueDates.size > 1;
    const dateHourMap = new Map<number, Array<{ date: string; clicks: number }>>();
    if (isMultiDay) {
        for (const r of byDateHour || []) {
            const h = Number(r.hour) || 0;
            const arr = dateHourMap.get(h) || [];
            arr.push({ date: r.date, clicks: Number(r.clicks) || 0 });
            dateHourMap.set(h, arr);
        }
    }
    const fmtDate = (iso: string) => {
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
        return m ? `${m[3]}/${m[2]}` : iso;
    };
    return (
        <div className="h-72">
            <ResponsiveContainer>
                <BarChart
                    data={data}
                    margin={{ top: 5, right: 5, bottom: 0, left: -15 }}
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#f1f5f9"
                        vertical={false}
                    />
                    <XAxis
                        dataKey="h"
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        tickLine={false}
                        axisLine={false}
                    />
                    <YAxis
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        tickLine={false}
                        axisLine={false}
                        tickCount={6}
                        tickFormatter={(v: number) =>
                            v >= 1000 ? (v / 1000).toFixed(1) + "k" : String(v)
                        }
                    />
                    <Tooltip
                        cursor={{ fill: "rgba(0,0,0,0.04)" }}
                        content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            const item = payload[0]?.payload as { hour: number; v: number };
                            const total = item?.v ?? 0;
                            const byDate = isMultiDay
                                ? (dateHourMap.get(item?.hour) || []).sort((a, b) => a.date.localeCompare(b.date))
                                : [];
                            return (
                                <div className="rounded-lg border border-gray-200 bg-white shadow-sm px-3 py-2 text-xs">
                                    <div className="font-semibold text-gray-900 mb-1">{label}h</div>
                                    {isMultiDay && byDate.length > 0 ? (
                                        <>
                                            <div className="space-y-0.5">
                                                {byDate.map((b) => (
                                                    <div key={b.date} className="flex items-center justify-between gap-3">
                                                        <span className="text-gray-500">{fmtDate(b.date)}</span>
                                                        <span className="font-mono font-medium text-gray-900">{fmt(b.clicks)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="mt-1 pt-1 border-t border-gray-100 flex items-center justify-between gap-3">
                                                <span className="text-gray-500">Total</span>
                                                <span className="font-mono font-semibold text-gray-900">{fmt(total)}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-gray-600">
                                            cliques: <span className="font-mono font-semibold text-gray-900">{fmt(total)}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        }}
                    />
                    <Bar
                        dataKey="v"
                        radius={[6, 6, 0, 0]}
                        minPointSize={3}
                    >
                        {data.map((row, i) => (
                            <Cell
                                key={i}
                                fill={
                                    row.hour === peakHour ? "#f59e0b" : "#8b5cf6"
                                }
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

// ── Hour × Weekday heatmap ───────────────────────────────────
interface HourHeatmapProps {
    cells: Array<{ weekday: number; hour: number; clicks: number }>;
}

function HourHeatmap({ cells }: HourHeatmapProps) {
    if (!cells.length) return <EmptyMsg />;
    const days = ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"];
    const max = Math.max(...cells.map((c) => c.clicks || 0), 1);
    return (
        <div
            className="grid gap-1"
            style={{ gridTemplateColumns: "40px repeat(24, 1fr)" }}
        >
            <div />
            {Array.from({ length: 24 }, (_, i) => (
                <div
                    key={i}
                    className="text-[10px] text-center text-gray-400 font-mono"
                >
                    {i % 2 === 0 ? String(i).padStart(2, "0") : ""}
                </div>
            ))}
            {Array.from({ length: 7 }, (_, r) => (
                <ResolvedRow key={r} row={r} day={days[r]} cells={cells} max={max} />
            ))}
        </div>
    );
}

function ResolvedRow({
    row,
    day,
    cells,
    max,
}: {
    row: number;
    day: string;
    cells: Array<{ weekday: number; hour: number; clicks: number }>;
    max: number;
}) {
    return (
        <>
            <div className="text-xs text-gray-500 font-medium flex items-center">
                {day}
            </div>
            {Array.from({ length: 24 }, (_, c) => {
                const cell = cells.find(
                    (x) => x.weekday === row && x.hour === c
                );
                const v = cell?.clicks || 0;
                const a = v / max;
                const isHot = a > 0.7;
                const bg =
                    v === 0
                        ? "rgba(241,245,249,1)"
                        : isHot
                          ? `rgba(245, 158, 11, ${a})`
                          : `rgba(139, 92, 246, ${a * 0.65})`;
                return (
                    <div
                        key={c}
                        title={`${day} ${c}h · ${fmt(v)}`}
                        className="rounded-sm"
                        style={{
                            height: 26,
                            background: bg,
                            border: "1px solid rgba(255,255,255,0.6)",
                        }}
                    />
                );
            })}
        </>
    );
}

function EmptyMsg() {
    return (
        <div className="text-center py-8">
            <AlertCircle className="w-6 h-6 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Sem dados no período</p>
        </div>
    );
}
